import React, {
  forwardRef, useEffect, useImperativeHandle,
} from 'react';
import {
  Box,
  Grid,
  MenuItem,
} from '@mui/material';
import CardWrapper from 'components/Financial/CardWrapper';
import * as Yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import TextFieldContext from 'components/CustomInputsInFormikContext/TextField';
import { HtmlTooltip } from 'components/tooltips/HtmlTootip';
import InfoIcon from '@mui/icons-material/Info';
import DataRangePickerContext from 'components/CustomInputsInFormikContext/DataRangePicker';
import SelectFieldContext from 'components/CustomInputsInFormikContext/Select';
import { isValidDate, labelField } from 'utils';
import { useLoadDataByLocation } from 'hooks/useLoadDataByLocation';

const validationSchema = Yup.object().shape({
  description: Yup.string().required('Campo obrigatório'),
  isInvestment: Yup.boolean().required('Campo obrigatório').nullable(),
  initial_date: Yup.string().nullable(),
  final_date: Yup.string().nullable(),
});

function Identification({ callback = () => {} }, ref) {
  const formik = useFormik({
    initialValues: {
      description: '',
      isInvestment: null,
      initial_date: null,
      final_date: null,
      type: 'pagamento',
    },
    validationSchema,
  });
  const [dateRange, setDateRange] = React.useState(null);

  function setData(data) {
    if (!data) return;

    formik.setValues({
      description: data.description,
      initial_date: isValidDate(data?.initial_date),
      final_date: isValidDate(data?.final_date),
      isInvestment: data.isInvestment,
      type: data.type,
    });

    setDateRange([isValidDate(data?.initial_date), isValidDate(data?.final_date)]);
  }

  useLoadDataByLocation({ setData });

  const handleSubmit = async () => {
    try {
      await formik.submitForm();
    } catch (e) {}
  };

  useEffect(() => {
    callback({ isValid: formik.isValid, values: formik.values });
  }, [callback, formik.isValid, formik.values]);

  // Permite que o componente pai chame o método handleSubmit
  useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

  return (
    <FormikProvider value={formik}>
      <CardWrapper title="Identificação">
        <Grid item xs={12} sm={6} md={4}>
          <TextFieldContext
            label={labelField('Descrição')}
            name="description"
            onBlur={() => formik.setFieldTouched('description', true)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <SelectFieldContext
              name="isInvestment"
              label={labelField('Finalidade')}
              InputLabelProps={{ shrink: formik.values.isInvestment !== null }}
            >
              <MenuItem key={1} value={false}>Pagamento</MenuItem>
              <MenuItem key={2} value>Investimento</MenuItem>
            </SelectFieldContext>
            <HtmlTooltip
              title={(
                <p>
                  <strong>Pagamento ou investimento?</strong>
                  <br />
                  <br />
                  Um pagamento representa a saída de dinheiro para cumprir
                  obrigações imediatas, como despesas operacionais ou quitação
                  de contas.
                  <br />
                  Investimento envolve destinar dinheiro para ativos financeiros
                  com a expectativa de obter lucro ou aumentar o valor do
                  investimento no longo prazo.
                </p>
              )}
              placement="right"
              arrow
              interactive
            >
              <InfoIcon
                sx={{ width: 24, height: 24 }}
                color="primary"
                fontSize="inherit"
              />
            </HtmlTooltip>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DataRangePickerContext
            inputLabel="Período"
            dateRange={dateRange}
            setDateRange={setDateRange}
            initialDateFieldName="initial_date"
            endDateFieldName="final_date"
          />
        </Grid>
      </CardWrapper>
    </FormikProvider>
  );
}

export default forwardRef(Identification);
